import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, TriangleAlert } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { AlertsAtCheckout, BorrowerAlert, CheckoutKind } from '../lib/checkouts';
import { Badge, cn, Spinner } from './ui';

/** The alerts on someone about to borrow (give the kind of check-out) or return something (give the checkout). */
export function useBorrowerAlerts(personId: string | null | undefined, options: { kind?: CheckoutKind; checkoutId?: string; enabled?: boolean } = {}) {
  const { can } = useAuth();
  return useQuery({
    queryKey: ['borrower-alerts', personId, options.kind ?? null, options.checkoutId ?? null],
    queryFn: () => api.get<{ alerts: BorrowerAlert[] }>(`/people/${personId}/alerts`, { kind: options.kind, checkoutId: options.checkoutId }),
    select: (data) => data.alerts,
    enabled: !!personId && can('TECHNICIAN') && (options.enabled ?? true),
  });
}

/** Whether a check-out can go ahead: alerts have to be read, and one that blocks needs an administrator to override it. */
export function alertGate(alerts: BorrowerAlert[] | undefined, confirmed: boolean, isAdmin: boolean) {
  const list = alerts ?? [];
  const blocking = list.some((alert) => alert.level === 'BLOCK');
  return { ok: list.length === 0 || (confirmed && (!blocking || isAdmin)), override: blocking && confirmed && isAdmin, blocking };
}

/**
 * The alerts on a borrower. Before a check-out someone confirms they've read them, or an administrator overrides one that
 * blocks; at a check-in they're shown for information.
 */
export function BorrowerAlertsPanel({
  alerts,
  loading,
  mode = 'checkout',
  confirmed = false,
  onConfirm,
  className,
}: {
  alerts: BorrowerAlert[] | undefined;
  loading?: boolean;
  mode?: 'checkout' | 'checkin';
  confirmed?: boolean;
  onConfirm?(value: boolean): void;
  className?: string;
}) {
  const { can } = useAuth();
  if (loading) {
    return (
      <p className={cn('flex items-center gap-2 text-sm text-slate-500', className)}>
        <Spinner className="size-4" /> Checking for alerts…
      </p>
    );
  }
  if (!alerts || alerts.length === 0) return null;
  const blocking = mode === 'checkout' && alerts.some((alert) => alert.level === 'BLOCK');
  const isAdmin = can('ADMIN');

  return (
    <div role="alert" className={cn('overflow-hidden rounded-lg border', blocking ? 'border-red-200 bg-red-50/70' : 'border-amber-200 bg-amber-50/70', className)}>
      <ul className="divide-y divide-black/5">
        {alerts.map((alert) => (
          <li key={alert.key} className="flex items-start gap-2.5 px-3.5 py-2.5 text-sm">
            {alert.level === 'BLOCK' ? (
              <ShieldAlert className="mt-0.5 size-4 shrink-0 text-red-600" />
            ) : (
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900">{alert.title}</p>
              {alert.detail && <p className="text-xs text-slate-600">{alert.detail}</p>}
            </div>
            {alert.level === 'BLOCK' && mode === 'checkout' && <Badge tone="red">Blocks check-out</Badge>}
          </li>
        ))}
      </ul>
      {mode === 'checkout' && onConfirm && (
        <div className="border-t border-black/5 bg-white/60 px-3.5 py-2.5">
          {blocking && !isAdmin ? (
            <p className="text-sm text-red-800">Only an administrator can override this and lend anyway.</p>
          ) : (
            <label className="flex items-start gap-2 text-sm text-slate-800">
              <input type="checkbox" checked={confirmed} onChange={(event) => onConfirm(event.target.checked)} className="mt-0.5 size-4 rounded border-slate-300" />
              <span>{blocking ? 'Override and lend anyway. The override is noted on the checkout.' : 'I’ve read these and want to go ahead.'}</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/** Marks a checkout that had alerts on the borrower when it went out. */
export function AlertsNote({ value }: { value: AlertsAtCheckout | null | undefined }) {
  if (!value || value.alerts.length === 0) return null;
  return (
    <span title={value.alerts.map((alert) => alert.title).join('\n')}>
      <Badge tone={value.overridden ? 'red' : 'amber'}>
        <TriangleAlert className="size-3" />
        {value.overridden ? 'Alert overridden' : `${value.alerts.length} alert${value.alerts.length === 1 ? '' : 's'}`}
      </Badge>
    </span>
  );
}
